/**
 * The Customers view's address: ?view=customers&acc=…&co=…[&cust=…]. The list
 * and a customer's page live in the address, so Back, reload and a link from a
 * work order land on the same thing.
 */
import { registerWorkOrderReturn, setWorkOrderReturn, type WorkOrderReturn } from '../../utils/workOrderReturn';

export const CUSTOMER_PARAM = 'cust';

/** The work order return kind for a customer's page. */
export const CUSTOMER_RETURN = 'customer';

/** "Back to this customer's page": what a work order opened from it closes to. */
export function customerReturn(opts: { accountId?: string | null; locationId?: string | null; customerId: string }): WorkOrderReturn {
  return {
    kind: CUSTOMER_RETURN,
    params: { acc: opts.accountId ?? '', co: opts.locationId ?? '', id: opts.customerId },
  };
}

/** Teaches the work order screen how to go back to a customer's page. */
export function registerCustomerWorkOrderReturn(): void {
  registerWorkOrderReturn(CUSTOMER_RETURN, (p) =>
    navigateToCustomers({ accountId: p.acc || null, locationId: p.co || null, customerId: p.id || null })
  );
}

/** The customer open in the address, if any. */
export function customerIdFromAddress(search: string = typeof window !== 'undefined' ? window.location.search : ''): string | null {
  return new URLSearchParams(search).get(CUSTOMER_PARAM) || null;
}

/**
 * Opens the Customers view (a customer's page when an id is given) for a store,
 * through the app's `navigate` event like the other in-app links. `replace`
 * swaps the current history entry instead of adding one — for a landing (the
 * dashboard the app opened on is not somewhere Back should return to).
 */
export function navigateToCustomers(opts: {
  accountId?: string | null;
  locationId?: string | null;
  customerId?: string | null;
  replace?: boolean;
}): void {
  const params = new URLSearchParams(window.location.search);
  params.set('view', 'customers');
  if (opts.accountId) params.set('acc', opts.accountId);
  if (opts.locationId) params.set('co', opts.locationId);
  if (opts.customerId) params.set(CUSTOMER_PARAM, opts.customerId);
  else params.delete(CUSTOMER_PARAM);
  // A work order open in the address, and where it goes back to, belong to
  // the work order view.
  params.delete('wo');
  setWorkOrderReturn(params, null);
  const state = {
    view: 'customers',
    ...(opts.accountId && { account: opts.accountId }),
    ...(opts.locationId && { location: opts.locationId }),
  };
  const url = `${window.location.pathname}?${params.toString()}`;
  if (opts.replace) window.history.replaceState(state, '', url);
  else window.history.pushState(state, '', url);
  window.dispatchEvent(new CustomEvent('navigate', { detail: state }));
}

/** Moves between the list and a customer's page within the view (no view change). */
export function setCustomerInAddress(customerId: string | null): void {
  const params = new URLSearchParams(window.location.search);
  if (customerId) params.set(CUSTOMER_PARAM, customerId);
  else params.delete(CUSTOMER_PARAM);
  window.history.pushState(window.history.state, '', `${window.location.pathname}?${params.toString()}`);
}
